/**
 * TanStack Query client + localStorage persistence. Render instantly from the
 * persisted snapshot, revalidate in the background. Persisted query data is
 * revived through the same Zod schema as its queryFn (so `z.coerce.date()`
 * fields become Date again); shapes that no longer parse are dropped and
 * refetch. Issue-status writes are offline-first mutations: optimistic, paused
 * while offline, resumed on reconnect and after a reload.
 */

import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { QueryClient } from "@tanstack/solid-query";
import type {
  PersistedClient,
  Persister,
} from "@tanstack/solid-query-persist-client";
import {
  type CurrentRunResponse,
  emptyRunSummary,
} from "contracts/api/crawl/run-read.responses";
import { PageDetailResponseSchema } from "contracts/api/dashboard/page-detail.responses";
import {
  type OverviewResponse,
  OverviewResponseSchema,
} from "contracts/api/dashboard/responses";
import type { Issue } from "contracts/api/issue/responses";
import { MetricDetailSchema } from "contracts/api/metric/responses";
import { WebsiteHealthSummaryListSchema } from "contracts/api/website/responses";
import { api } from "./api";

const DAY_MS = 24 * 60 * 60 * 1000;

/** Bump to drop every persisted snapshot after a breaking cache-shape change. */
export const CACHE_BUSTER = "v2";
export const CACHE_MAX_AGE = DAY_MS;

/** queryKey[0] → reviver for persisted data. Only these keys are persisted. */
const REVIVERS: Record<string, (raw: unknown) => unknown> = {
  websites: (raw) => WebsiteHealthSummaryListSchema.parse(raw),
  overview: (raw) => OverviewResponseSchema.parse(raw),
  "page-detail": (raw) => PageDetailResponseSchema.parse(raw),
  "metric-detail": (raw) => MetricDetailSchema.parse(raw),
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // SWR: cached data renders immediately, a fresh fetch always runs on
      // mount. Focus refetches stay off to match the previous cache behavior.
      staleTime: 0,
      gcTime: CACHE_MAX_AGE,
      refetchOnWindowFocus: false,
      retry: 1,
    },
    mutations: {
      // Outbox parity ("save it first"): a write keeps retrying with backoff
      // while the tab is online but the backend is unreachable, and pauses
      // (and persists, surviving reloads) while the browser is offline.
      networkMode: "offlineFirst",
      retry: 30,
      retryDelay: (attempt: number) => Math.min(1000 * 2 ** attempt, 30 * 1000),
    },
  },
});

if (import.meta.env.DEV) {
  // Debug handles for live tests (scratchpad scripts) and console poking.
  const debugWindow = window as unknown as {
    faroQueryClient?: QueryClient;
    faroDehydrate?: () => unknown;
  };
  debugWindow.faroQueryClient = queryClient;
  debugWindow.faroDehydrate = async () => {
    const { dehydrate } = await import("@tanstack/solid-query");
    return dehydrate(queryClient);
  };
}

export type IssueStatusInput = {
  websiteId: string;
  issueId: string;
  status: Issue["status"];
};

// Registered as defaults (not inline in useMutation) so mutations persisted
// while offline can find their mutationFn again after a reload.
queryClient.setMutationDefaults(["issue-status"], {
  mutationFn: async (input: IssueStatusInput) => {
    await api.setIssueStatus(input.issueId, input.status);
  },
  onMutate: (input: IssueStatusInput) => {
    queryClient.setQueryData<OverviewResponse>(
      ["overview", input.websiteId],
      (current) =>
        current && {
          ...current,
          issues: current.issues.map((row) =>
            row.id === input.issueId ? { ...row, status: input.status } : row,
          ),
        },
    );
  },
});

export type ScanInput = { websiteId: string };

// "Run scan" seeds the current-run cache with a synthetic running run so the
// top bar flips instantly; the settle-time invalidation swaps in the real run
// (with its runId, route, and maxPages) from the backend. Scans are not
// outbox writes: a failed POST surfaces immediately instead of retrying for
// minutes.
queryClient.setMutationDefaults(["scan"], {
  networkMode: "online",
  retry: 1,
  mutationFn: async (input: ScanInput) => {
    await api.scan(input.websiteId);
  },
  onMutate: (input: ScanInput) => {
    queryClient.setQueryData<CurrentRunResponse>(
      ["current-run", input.websiteId],
      { run: emptyRunSummary(input.websiteId), yeetWorker: null },
    );
  },
  onSettled: (_data, _error, input: ScanInput) =>
    queryClient.invalidateQueries({
      queryKey: ["current-run", input.websiteId],
    }),
});

export const queryPersister: Persister = createSyncStoragePersister({
  storage: localStorage,
  key: "faro:query:scoped",
  deserialize: (cached) => revivePersistedClient(JSON.parse(cached)),
});

export function shouldPersistQueryKey(queryKey: readonly unknown[]): boolean {
  return typeof queryKey[0] === "string" && queryKey[0] in REVIVERS;
}

function revivePersistedClient(persisted: PersistedClient): PersistedClient {
  return {
    ...persisted,
    clientState: {
      ...persisted.clientState,
      queries: persisted.clientState.queries.flatMap((query) => {
        const revive = REVIVERS[String(query.queryKey[0])];
        if (!revive) {
          return [];
        }
        try {
          return [
            {
              ...query,
              state: { ...query.state, data: revive(query.state.data) },
            },
          ];
        } catch {
          // Stale shape from an older deploy: drop it, the query refetches.
          return [];
        }
      }),
    },
  };
}
